import { useState, useEffect } from 'react';
import { Plus, Calendar, Search, Filter, Eye, Edit, CheckCircle, Clock, Play, Package, AlertTriangle } from 'lucide-react';
import { mantenimientosApi, busesApi, mecanicosApi, stockApi } from '../lib/api';
import { toast } from 'sonner';
import { fechaLima } from '../lib/fechas';
import { coincideBusqueda } from '../lib/buscar';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const MantenimientosCompleto = () => {
  const [mantenimientos, setMantenimientos] = useState<any[]>([]);
  const [buses, setBuses] = useState<any[]>([]);
  const [mecanicos, setMecanicos] = useState<any[]>([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarDetalles, setMostrarDetalles] = useState(false);
  const [mantenimientoSeleccionado, setMantenimientoSeleccionado] = useState<any>(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroCategoria, setFiltroCategoria] = useState('todos');
  const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
  const [filtroFechaFin, setFiltroFechaFin] = useState('');
  const [mecanicosSeleccionados, setMecanicosSeleccionados] = useState<string[]>([]);
  const [mecanicoExterno, setMecanicoExterno] = useState('');
  const [usarMecanicoExterno, setUsarMecanicoExterno] = useState(false);
  const [buscadorBus, setBuscadorBus] = useState('');
  const [mostrarListaBuses, setMostrarListaBuses] = useState(false);
  const [mostrarStock, setMostrarStock] = useState(false);
  const [stockItems, setStockItems] = useState<any[]>([]);
  const [busquedaStock, setBusquedaStock] = useState('');
  const [cargandoStock, setCargandoStock] = useState(false);
  const [marcarComoCompletado, setMarcarComoCompletado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [nuevoMantenimiento, setNuevoMantenimiento] = useState({
    tipo: 'correctivo',
    bus_id: '',
    categoria: 'otros',
    descripcion: '',
    fecha_programada: fechaLima(new Date()),
    fecha_atencion: '',
    turno: 'dia',
    mecanico_id: '',
    observaciones: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  // Cerrar lista de buses al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.bus-search-container')) {
        setMostrarListaBuses(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para formatear fechas de manera segura
  const formatearFecha = (fecha: string | null | undefined) => {
    if (!fecha || fecha.trim() === '' || fecha === 'null' || fecha === 'undefined') {
      return 'No definida';
    }
    
    try {
      let fechaObj;
      
      // Si la fecha ya incluye hora, usarla directamente
      if (fecha.includes('T')) {
        fechaObj = new Date(fecha);
      } else {
        // Si es solo fecha, agregar hora para evitar problemas de timezone
        fechaObj = new Date(fecha + 'T12:00:00');
      }
      
      // Verificar si la fecha es válida
      if (isNaN(fechaObj.getTime())) {
        return 'Fecha inválida';
      }
      
      return fechaObj.toLocaleDateString('es-PE');
      
    } catch (error) {
      console.error('Error al formatear fecha:', fecha, error);
      return 'Fecha inválida';
    }
  };

  const cargarDatos = async () => {
    try {
      const [mantData, busData, mecData] = await Promise.all([
        mantenimientosApi.getAll(),
        busesApi.getAll(),
        mecanicosApi.getAll()
      ]);

      if (mantData) setMantenimientos(mantData);
      if (busData) setBuses(busData);
      if (mecData) setMecanicos(mecData);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos');
    }
  };

  const cargarStock = async () => {
    setCargandoStock(true);
    try {
      const data = await stockApi.getAll();
      setStockItems(data || []);
    } catch (error) {
      console.error('Error cargando stock:', error);
      toast.error('Error al cargar el stock');
    } finally {
      setCargandoStock(false);
    }
  };

  useEffect(() => {
    if (mostrarStock) {
      cargarStock();
    }
  }, [mostrarStock]);

  const agregarMantenimiento = async () => {
    if (guardando) return;
    setGuardando(true);
    try {
      // Validar que haya al menos un mecánico o mecánico externo
      if (!usarMecanicoExterno && mecanicosSeleccionados.length === 0) {
        toast.error('Debes seleccionar al menos un mecánico o usar mecánico externo');
        return;
      }

      if (usarMecanicoExterno && !mecanicoExterno.trim()) {
        toast.error('Debes ingresar el nombre del mecánico externo');
        return;
      }

      // Validar bus
      if (!nuevoMantenimiento.bus_id) {
        toast.error('Debes seleccionar un bus');
        return;
      }

      // Validar descripción
      if (!nuevoMantenimiento.descripcion.trim()) {
        toast.error('Debes ingresar una descripción');
        return;
      }

      // Preparar datos de mecánicos
      const mecanicosData = usarMecanicoExterno 
        ? mecanicoExterno.trim()
        : mecanicosSeleccionados.join(',');

      // Obtener el bus para generar el número de mantenimiento
      const busSeleccionado = buses.find(b => b.id === nuevoMantenimiento.bus_id);
      const padron = busSeleccionado?.padron || busSeleccionado?.numero || 'BUS';
      const timestamp = Date.now().toString().slice(-6);
      const numero = `MNT-${padron}-${timestamp}`;
      
      // Generar título automático basado en la categoría
      const titulos: { [key: string]: string } = {
        'motor': 'Mantenimiento de Motor',
        'frenos': 'Mantenimiento de Frenos',
        'embrague': 'Mantenimiento de Embrague',
        'cambio_aceite': 'Cambio de Aceite',
        'conjunto_cardanico': 'Mantenimiento de Conjunto Cardánico',
        'corona': 'Mantenimiento de Corona',
        'caja': 'Mantenimiento de Caja',
        'aire': 'Mantenimiento de Sistema de Aire',
        'sist_electrico': 'Mantenimiento de Sistema Eléctrico',
        'otros': 'Mantenimiento General'
      };
      const titulo = titulos[nuevoMantenimiento.categoria] || 'Mantenimiento';

      // Crear objeto base
      let datosMantenimiento: any = {
        numero: numero, // CAMPO REQUERIDO
        titulo: titulo, // CAMPO REQUERIDO 
        tipo: nuevoMantenimiento.tipo,
        bus_id: nuevoMantenimiento.bus_id,
        categoria: nuevoMantenimiento.categoria,
        descripcion: nuevoMantenimiento.descripcion,
        fecha_programada: nuevoMantenimiento.fecha_programada,
        turno: nuevoMantenimiento.turno,
        mecanico_id: mecanicosSeleccionados[0] || null,
        mecanicos: mecanicosData,
        mecanico_personalizado: usarMecanicoExterno ? mecanicoExterno.trim() : null,
        observaciones: nuevoMantenimiento.observaciones,
        // LÓGICA DE COMPLETADO AUTOMÁTICO:
        // - Si es PREVENTIVO: siempre se crea como "programado" (se marca manualmente después)
        // - Si es CORRECTIVO y el checkbox está marcado: se crea como "completado" con fecha actual
        // - Si es CORRECTIVO y el checkbox NO está marcado: se crea como "programado"
        estado: nuevoMantenimiento.tipo === 'preventivo' 
          ? 'programado' 
          : (marcarComoCompletado ? 'completado' : 'programado')
      };

      // Agregar fecha_atencion automáticamente si el mantenimiento se marca como completado
      if (marcarComoCompletado && nuevoMantenimiento.tipo !== 'preventivo') {
        // Si es correctivo y está marcado como completado, usar fecha_atencion o fecha actual
        datosMantenimiento.fecha_atencion = nuevoMantenimiento.fecha_atencion || fechaLima();
      } else if (nuevoMantenimiento.fecha_atencion) {
        // Si se ingresó una fecha de atención manualmente, usarla
        datosMantenimiento.fecha_atencion = nuevoMantenimiento.fecha_atencion;
      }

      console.log('Datos a insertar:', datosMantenimiento);

      await mantenimientosApi.create(datosMantenimiento);

      toast.success('Mantenimiento creado exitosamente');
      setMostrarModal(false);
      setBuscadorBus('');
      setMostrarListaBuses(false);
      cargarDatos();
      
      // Generar ambos reportes automáticamente
      if (busSeleccionado) {
        const padronReporte = busSeleccionado.padron || busSeleccionado.numero;
        
        // Generar PDF en background
        fetch(`${API_URL}/api/reportes/bus-mantenimiento/${padronReporte}/pdf`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }).catch(err => console.error('Error generando PDF automático:', err));
        
        // Generar TXT en background
        fetch(`${API_URL}/api/reportes/bus-mantenimiento/${padronReporte}/txt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }).catch(err => console.error('Error generando TXT automático:', err));
        
        console.log(`✅ Reportes generados automáticamente para Padrón ${padronReporte}`);
      }
      
      // Resetear formulario
      setNuevoMantenimiento({
        tipo: 'correctivo',
        bus_id: '',
        categoria: 'otros',
        descripcion: '',
        fecha_programada: fechaLima(new Date()),
        fecha_atencion: '',
        turno: 'dia',
        mecanico_id: '',
        observaciones: ''
      });
      setMecanicosSeleccionados([]);
      setMecanicoExterno('');
      setUsarMecanicoExterno(false);
      setMarcarComoCompletado(false);
    } catch (error) {
      console.error('Error agregando mantenimiento:', error);
      toast.error('Error al crear mantenimiento');
    } finally {
      setGuardando(false);
    }
  };

  const verDetalles = (mantenimiento: any) => {
    setMantenimientoSeleccionado(mantenimiento);
    setModoEdicion(false);
    setMostrarDetalles(true);
  };

  const editarMantenimiento = (mantenimiento: any) => {
    setMantenimientoSeleccionado(mantenimiento);
    setNuevoMantenimiento({
      tipo: mantenimiento.tipo,
      bus_id: mantenimiento.bus_id,
      categoria: mantenimiento.categoria || 'otros',
      descripcion: mantenimiento.descripcion,
      fecha_programada: mantenimiento.fecha_programada,
      fecha_atencion: mantenimiento.fecha_atencion || '',
      turno: mantenimiento.turno,
      mecanico_id: mantenimiento.mecanico_id || '',
      observaciones: mantenimiento.observaciones || ''
    });
    
    // Cargar mecánicos seleccionados
    if (mantenimiento.mecanicos) {
      // Si es mecánico externo (texto sin UUIDs)
      if (!mantenimiento.mecanicos.includes(',') && !mantenimiento.mecanicos.match(/^[0-9a-f-]+$/)) {
        setUsarMecanicoExterno(true);
        setMecanicoExterno(mantenimiento.mecanicos);
        setMecanicosSeleccionados([]);
      } else {
        // Si son IDs de mecánicos
        const ids = mantenimiento.mecanicos.split(',').map((id: string) => id.trim());
        setMecanicosSeleccionados(ids);
        setUsarMecanicoExterno(false);
        setMecanicoExterno('');
      }
    } else if (mantenimiento.mecanico_id) {
      // Fallback al campo antiguo
      setMecanicosSeleccionados([mantenimiento.mecanico_id]);
      setUsarMecanicoExterno(false);
      setMecanicoExterno('');
    } else {
      setMecanicosSeleccionados([]);
      setUsarMecanicoExterno(false);
      setMecanicoExterno('');
    }
    
    // Configurar buscador de buses
    const bus = buses.find(b => b.id === mantenimiento.bus_id);
    if (bus) {
      const numeroPadron = bus.numero || bus.padron || bus.id;
      const nombreBus = `Padrón ${numeroPadron} - ${bus.placa}`;
      setBuscadorBus(nombreBus);
    }
    
    setModoEdicion(true);
    setMostrarModal(true);
  };

  const actualizarMantenimiento = async () => {
    try {
      // Preparar datos de mecánicos
      const mecanicosData = usarMecanicoExterno 
        ? mecanicoExterno.trim()
        : mecanicosSeleccionados.join(',');

      await mantenimientosApi.update(mantenimientoSeleccionado.id, {
        ...nuevoMantenimiento,
        mecanico_id: mecanicosSeleccionados[0] || null,
        mecanicos: mecanicosData,
        mecanico_personalizado: usarMecanicoExterno ? mecanicoExterno.trim() : null
      });

      toast.success('Mantenimiento actualizado exitosamente');
      setMostrarModal(false);
      setBuscadorBus('');
      setMostrarListaBuses(false);
      setModoEdicion(false);
      setMantenimientoSeleccionado(null);
      cargarDatos();
      setNuevoMantenimiento({
        tipo: 'correctivo',
        bus_id: '',
        categoria: 'otros',
        descripcion: '',
        fecha_programada: fechaLima(new Date()),
        fecha_atencion: '',
        turno: 'dia',
        mecanico_id: '',
        observaciones: ''
      });
      setMecanicosSeleccionados([]);
      setMecanicoExterno('');
      setUsarMecanicoExterno(false);
    } catch (error) {
      console.error('Error actualizando mantenimiento:', error);
      toast.error('Error al actualizar mantenimiento');
    }
  };

  const toggleMecanico = (mecanicoId: string) => {
    setMecanicosSeleccionados(prev => {
      if (prev.includes(mecanicoId)) {
        return prev.filter(id => id !== mecanicoId);
      } else {
        return [...prev, mecanicoId];
      }
    });
  };

  const cambiarEstado = async (id: string, nuevoEstado: 'pendiente' | 'en_proceso' | 'completado') => {
    try {
      await mantenimientosApi.update(id, { estado: nuevoEstado });

      toast.success(`Estado cambiado a ${nuevoEstado === 'pendiente' ? 'Pendiente' : nuevoEstado === 'en_proceso' ? 'En Proceso' : 'Completado'}`);
      cargarDatos();
      
      // Actualizar el mantenimiento seleccionado si está en el modal
      if (mantenimientoSeleccionado?.id === id) {
        setMantenimientoSeleccionado({ ...mantenimientoSeleccionado, estado: nuevoEstado });
      }
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast.error('Error al cambiar estado');
    }
  };

  // Funciones del buscador de buses
  const buscarBuses = (termino: string) => {
    console.log('🔍 Buscando buses con término:', termino);
    console.log('📊 Total buses disponibles:', buses.length);
    
    if (!termino.trim()) {
      console.log('🎯 Término vacío, mostrando todos los buses');
      return buses;
    }
    
    const resultados = buses.filter(bus => {
      const padron = String(bus.numero || bus.padron || bus.id);
      const placa = String(bus.placa || '');
      const marca = String(bus.marca || '');
      
      const coincidePadron = padron.toLowerCase().includes(termino.toLowerCase());
      const coincidePlaca = placa.toLowerCase().includes(termino.toLowerCase());
      const coincideMarca = marca.toLowerCase().includes(termino.toLowerCase());
      
      return coincidePadron || coincidePlaca || coincideMarca;
    });
    
    console.log('✅ Buses encontrados:', resultados.length);
    return resultados;
  };

  const seleccionarBus = (bus: any) => {
    console.log('🚌 Bus seleccionado:', bus);
    setNuevoMantenimiento({...nuevoMantenimiento, bus_id: bus.id});
    
    // Crear el nombre del bus directamente desde el objeto
    const numeroPadron = bus.numero || bus.padron || bus.id;
    const nombreBus = `Padrón ${numeroPadron} - ${bus.placa}`;
    
    console.log('📝 Nombre generado:', nombreBus);
    setBuscadorBus(nombreBus);
    setMostrarListaBuses(false);
  };

  const limpiarBuscadorBus = () => {
    setBuscadorBus('');
    setNuevoMantenimiento({...nuevoMantenimiento, bus_id: ''});
    setMostrarListaBuses(false);
  };

  const mantenimientosFiltrados = mantenimientos.filter(m => {
    const bus = buses.find(b => b.id === m.bus_id);
    
    // Extraer número de padrón para búsqueda
    let numeroPadron = '';
    if (bus?.numero) {
      numeroPadron = String(bus.numero);
    } else if (bus?.padron) {
      if (typeof bus.padron === 'string' && bus.padron.includes('Padrón -')) {
        const match = bus.padron.match(/Padrón\s*-\s*([A-Z0-9-]+)/);
        numeroPadron = match ? match[1].trim() : String(bus.padron);
      } else {
        numeroPadron = String(bus.padron);
      }
    }
    
    // Filtro de búsqueda (descripción, observaciones, padrón, placa)
    const coincideBusqueda = busqueda === '' || 
      m.descripcion?.toLowerCase().includes(busqueda.toLowerCase()) ||
      m.observaciones?.toLowerCase().includes(busqueda.toLowerCase()) ||
      numeroPadron.toLowerCase().includes(busqueda.toLowerCase()) ||
      bus?.placa?.toLowerCase().includes(busqueda.toLowerCase()) ||
      m.categoria?.toLowerCase().includes(busqueda.toLowerCase());
    
    // Filtro de estado
    const coincideEstado = filtroEstado === 'todos' || m.estado === filtroEstado;
    
    // Filtro de categoría
    const coincideCategoria = filtroCategoria === 'todos' || m.categoria === filtroCategoria;
    
    // Filtro de fecha inicio
    const coincideFechaInicio = filtroFechaInicio === '' || 
      (m.fecha_programada && new Date(m.fecha_programada) >= new Date(filtroFechaInicio));
    
    // Filtro de fecha fin
    const coincideFechaFin = filtroFechaFin === '' || 
      (m.fecha_programada && new Date(m.fecha_programada) <= new Date(filtroFechaFin));
    
    return coincideBusqueda && coincideEstado && coincideCategoria && 
           coincideFechaInicio && coincideFechaFin;
  });

  const getBusNombre = (busId: string) => {
    const bus = buses.find(b => b.id === busId);
    if (!bus) return 'N/A';
    
    // Extraer el número de padrón correctamente
    let numeroPadron = '';
    
    if (bus.numero) {
      numeroPadron = String(bus.numero);
    } else if (bus.padron) {
      // Si contiene "Padrón -", extraer solo la parte del código
      if (typeof bus.padron === 'string' && bus.padron.includes('Padrón -')) {
        const match = bus.padron.match(/Padrón\s*-\s*([A-Z0-9-]+)/);
        if (match) {
          numeroPadron = match[1].trim();
        } else {
          numeroPadron = String(bus.padron);
        }
      } else {
        numeroPadron = String(bus.padron);
      }
    } else {
      numeroPadron = bus.id;
    }
    
    return `Padrón ${numeroPadron} - ${bus.placa}`;
  };

  const getMecanicoNombre = (mecId: string) => {
    const mec = mecanicos.find(m => m.id === mecId);
    return mec ? mec.nombre : 'No asignado';
  };

  const getMecanicosNombres = (mantenimiento: any) => {
    // Si tiene el campo mecanicos (nuevo), usarlo
    if (mantenimiento.mecanicos) {
      // Si es texto (mecánico externo)
      if (!mantenimiento.mecanicos.includes(',') && !mantenimiento.mecanicos.match(/^[0-9a-f-]+$/)) {
        return mantenimiento.mecanicos;
      }
      // Si son IDs separados por coma
      const ids = mantenimiento.mecanicos.split(',');
      const nombres = ids.map((id: string) => {
        const mec = mecanicos.find(m => m.id === id.trim());
        return mec ? mec.nombre : id;
      }).filter(Boolean);
      return nombres.join(', ') || 'No asignado';
    }
    // Fallback al campo antiguo mecanico_id
    if (mantenimiento.mecanico_id) {
      return getMecanicoNombre(mantenimiento.mecanico_id);
    }
    return 'No asignado';
  };

  // Generar Reporte PDF completo para taller
  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'programado': return 'bg-blue-100 text-blue-800';
      case 'en_proceso': return 'bg-yellow-100 text-yellow-800';
      case 'completado': return 'bg-green-100 text-green-800';
      case 'atendido': return 'bg-green-100 text-green-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (m: any) => {
    if (m.fecha_atencion && m.fecha_atencion.trim() !== '') return 'ATENDIDO';
    return (m.estado || 'programado').toUpperCase();
  };

  const diasParaProximo = (fecha_programada: string) => {
    if (!fecha_programada) return null;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const prox = new Date(fecha_programada);
    prox.setHours(0, 0, 0, 0);
    const diff = Math.ceil((prox.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Mantenimientos</h1>
          <p className="text-gray-600 text-sm mt-1">
            {mantenimientos.length} mantenimientos registrados
          </p>
        </div>
        <button
          onClick={() => {
            setNuevoMantenimiento({
              tipo: 'correctivo',
              bus_id: '',
              categoria: 'otros',
              descripcion: '',
              fecha_programada: fechaLima(new Date()),
              fecha_atencion: '',
              turno: 'dia',
              mecanico_id: '',
              observaciones: ''
            });
            setBuscadorBus('');
            setMostrarListaBuses(false);
            setModoEdicion(false);
            setMantenimientoSeleccionado(null);
            setMostrarModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus size={20} />
          Nuevo Mantenimiento
        </button>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por padrón, placa, descripción..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          
          {/* Filtro Estado */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            >
              <option value="todos">Todos los estados</option>
              <option value="programado">📅 Programados</option>
              <option value="en_proceso">🔧 En Proceso</option>
              <option value="completado">✅ Completados</option>
              <option value="cancelado">❌ Cancelados</option>
            </select>
          </div>
          
          {/* Filtro Categoría */}
          <div className="relative">
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="todos">Todas las categorías</option>
                    {nuevoMantenimiento.tipo === 'preventivo' ? (
                      <>
                        <option value="cambio_aceite">🛢️ Cambio de Aceite</option>
                        <option value="frenos">🛑 Frenos</option>
                        <option value="otros">📋 Otros</option>
                      </>
                    ) : (
                      <>
                        <option value="embrague">🔧 Embrague</option>
                        <option value="frenos">🛑 Frenos</option>
                        <option value="motor">⚙️ Motor</option>
                        <option value="cambio_aceite">🛢️ Cambio de Aceite</option>
                        <option value="conjunto_cardanico">🔩 Conjunto Cardánico</option>
                        <option value="corona">👑 Corona</option>
                        <option value="caja">📦 Caja</option>
                        <option value="aire">💨 Aire</option>
                        <option value="sist_electrico">⚡ Sist Eléctrico</option>
                        <option value="otros">📋 Otros</option>
                      </>
                    )}
              <option value="otros">📋 Otros</option>
            </select>
          </div>
          
          {/* Botón limpiar filtros */}
          <div>
            <button
              onClick={() => {
                setBusqueda('');
                setFiltroEstado('todos');
                setFiltroCategoria('todos');
                setFiltroFechaInicio('');
                setFiltroFechaFin('');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
        
        {/* Filtros de fecha */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha desde:
            </label>
            <input
              type="date"
              value={filtroFechaInicio}
              onChange={(e) => setFiltroFechaInicio(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha hasta:
            </label>
            <input
              type="date"
              value={filtroFechaFin}
              onChange={(e) => setFiltroFechaFin(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Lista de mantenimientos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {mantenimientosFiltrados.map(m => (
          <div key={m.id} className={`bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow ${(() => { const noAtendido = !m.fecha_atencion || m.fecha_atencion.trim() === ''; const d = diasParaProximo(m.fecha_programada); return noAtendido && d !== null && d <= 3 ? 'border-2 border-orange-400' : ''; })()}`}>
            {(() => {
              const noAtendido = !m.fecha_atencion || m.fecha_atencion.trim() === '';
              const dias = diasParaProximo(m.fecha_programada);
              if (noAtendido && dias !== null && dias <= 3) {
                return (
                  <div className="flex items-center gap-2 bg-orange-100 border border-orange-400 text-orange-800 rounded px-3 py-2 mb-3 text-sm font-bold">
                    <AlertTriangle size={15} />
                    {dias < 0
                      ? `⚠️ ATENCIÓN PROGRAMADA — VENCIDO (${Math.abs(dias)} día${Math.abs(dias) === 1 ? '' : 's'} de retraso)`
                      : dias === 0
                      ? '⚠️ ATENCIÓN PROGRAMADA — ¡HOY!'
                      : `⚠️ ATENCIÓN PROGRAMADA — en ${dias} día${dias === 1 ? '' : 's'}`}
                  </div>
                );
              }
              return null;
            })()}
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-bold text-lg">{getBusNombre(m.bus_id)}</h3>
                <p className="text-sm text-gray-500 capitalize">Mantenimiento {m.tipo || ''}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getEstadoLabel(m) === 'ATENDIDO' ? 'bg-green-100 text-green-800' : getEstadoColor(m.estado)}`}>
                {getEstadoLabel(m)}
              </span>
            </div>
            
            <p className="text-gray-700 mb-3 line-clamp-2">{m.descripcion}</p>
            
            <div className="space-y-2 text-sm text-gray-600">
              {/* PRIMER: Mostrar fecha REALIZADA (fecha_atencion) */}
              <div className="flex items-center gap-2">
                <span className="text-green-600">🔧</span>
                <span className="font-medium">Fecha Realizada:</span>
                <span className={m.fecha_atencion && m.fecha_atencion.trim() !== '' ? "text-green-600 font-semibold" : "text-gray-400"}>
                  {m.fecha_atencion && m.fecha_atencion.trim() !== '' 
                    ? formatearFecha(m.fecha_atencion) 
                    : "Pendiente"}
                </span>
              </div>
              {/* SEGUNDA: Mostrar fecha PROGRAMADA */}
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span className="font-medium">Fecha Programada:</span>
                <span>{formatearFecha(m.fecha_programada)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Tipo:</span>
                <span className="capitalize">{m.tipo}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Mecánico(s):</span>
                <span>{getMecanicosNombres(m)}</span>
              </div>
              {m.costo && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Costo:</span>
                  <span className="text-green-600 font-bold">S/ {m.costo}</span>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <button 
                onClick={() => verDetalles(m)}
                className="flex-1 px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
              >
                <Eye size={16} className="inline mr-1" />
                Ver
              </button>
              <button 
                onClick={() => editarMantenimiento(m)}
                className="flex-1 px-3 py-1 text-sm bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
              >
                <Edit size={16} className="inline mr-1" />
                Editar
              </button>
            </div>
          </div>
        ))}
      </div>

      {mantenimientosFiltrados.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No se encontraron mantenimientos</p>
        </div>
      )}

      {/* Modal Nuevo/Editar Mantenimiento */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {modoEdicion ? 'Editar Mantenimiento' : 'Nuevo Mantenimiento'}
              </h3>
              <button
                type="button"
                onClick={() => setMostrarStock(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
              >
                <Package size={16} />
                Ver Stock
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo *</label>
                  <select
                    value={nuevoMantenimiento.tipo}
                    onChange={(e) => {
                      const newTipo = e.target.value;
                      setNuevoMantenimiento({
                        ...nuevoMantenimiento,
                        tipo: newTipo,
                        categoria: newTipo === 'preventivo' ? 'cambio_aceite' : 'otros'
                      });
                      // Si cambia a preventivo, desmarcar el completado automático
                      if (newTipo === 'preventivo') {
                        setMarcarComoCompletado(false);
                      }
                    }}
                    className="w-full p-2 border rounded"
                  >
                    <option value="correctivo">Correctivo</option>
                    <option value="preventivo">Preventivo</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Bus (Padrón) *</label>
                  <div className="relative bus-search-container">
                    <input
                      type="text"
                      placeholder="Buscar por padrón o placa..."
                      value={buscadorBus}
                      onChange={(e) => {
                        setBuscadorBus(e.target.value);
                        setMostrarListaBuses(true);
                      }}
                      onFocus={() => {
                        console.log('🎯 Input enfocado, mostrando lista de buses');
                        setMostrarListaBuses(true);
                      }}
                      onClick={() => {
                        console.log('🖱️ Click en input, forzando mostrar lista');
                        setMostrarListaBuses(true);
                      }}
                      className="w-full p-2 border rounded pr-10"
                      autoComplete="off"
                    />
                    {buscadorBus && (
                      <button
                        type="button"
                        onClick={limpiarBuscadorBus}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    )}
                    
                    {/* Lista desplegable de buses */}
                    {mostrarListaBuses && (
                      <div className="absolute z-20 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                        {buses.length === 0 ? (
                          <div className="p-3 text-center">
                            <div className="text-gray-500 mb-2">⚠️ No hay buses disponibles</div>
                            <div className="text-xs text-gray-400">
                              Verifique la conexión a la base de datos
                            </div>
                          </div>
                        ) : buscarBuses(buscadorBus).length === 0 ? (
                          <div className="p-3 text-gray-500 text-center">
                            {buscadorBus.trim() ? `No se encontraron buses para "${buscadorBus}"` : 'No hay buses registrados'}
                          </div>
                        ) : (
                          <>
                            {!buscadorBus.trim() && (
                              <div className="p-2 bg-blue-50 text-blue-700 text-sm border-b">
                                💡 {buses.length} buses disponibles - Escriba para buscar
                              </div>
                            )}
                            {buscarBuses(buscadorBus).map(bus => {
                              const numeroPadron = bus.numero || bus.padron || bus.id;
                              return (
                                <div
                                  key={bus.id}
                                  className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                                  onClick={() => seleccionarBus(bus)}
                                >
                                  <div className="font-medium">Padrón: {numeroPadron}</div>
                                  <div className="text-sm text-gray-600">Placa: {bus.placa}</div>
                                  <div className="text-xs text-gray-500">{bus.marca} {bus.modelo}</div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Categoría *</label>
                  <select
                    value={nuevoMantenimiento.categoria}
                    onChange={(e) => setNuevoMantenimiento({...nuevoMantenimiento, categoria: e.target.value})}
                    className="w-full p-2 border rounded"
                    required
                  >
                    {nuevoMantenimiento.tipo === 'preventivo' ? (
                      <>
                        <option value="cambio_aceite">🛢️ Cambio de Aceite</option>
                        <option value="frenos">🛑 Frenos</option>
                        <option value="embrague">🔧 Embrague</option>
                      </>
                    ) : (
                      <>
                        <option value="embrague">🔧 Embrague</option>
                        <option value="frenos">🛑 Frenos</option>
                        <option value="motor">⚙️ Motor</option>
                        <option value="cambio_aceite">🛢️ Cambio de Aceite</option>
                        <option value="conjunto_cardanico">🔩 Conjunto Cardánico</option>
                        <option value="corona">👑 Corona</option>
                        <option value="caja">📦 Caja</option>
                        <option value="aire">💨 Aire</option>
                        <option value="sist_electrico">⚡ Sist Eléctrico</option>
                        <option value="otros">📋 Otros</option>
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Fecha de Atención</label>
                  <input
                    type="date"
                    value={nuevoMantenimiento.fecha_atencion}
                    onChange={(e) => setNuevoMantenimiento({...nuevoMantenimiento, fecha_atencion: e.target.value})}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">🔧 Fecha cuando realmente se realizó el mantenimiento (opcional)</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Turno *</label>
                  <select
                    value={nuevoMantenimiento.turno}
                    onChange={(e) => setNuevoMantenimiento({...nuevoMantenimiento, turno: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="dia">Día (6 AM - 6 PM)</option>
                    <option value="noche">Noche (6 PM - 6 AM)</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Mecánicos *</label>
                  
                  {/* Opción de Mecánico Externo */}
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={usarMecanicoExterno}
                        onChange={(e) => {
                          setUsarMecanicoExterno(e.target.checked);
                          if (e.target.checked) {
                            setMecanicosSeleccionados([]);
                          } else {
                            setMecanicoExterno('');
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="font-medium text-blue-700">Mecánico Externo</span>
                    </label>
                    
                    {usarMecanicoExterno && (
                      <input
                        type="text"
                        value={mecanicoExterno}
                        onChange={(e) => setMecanicoExterno(e.target.value)}
                        placeholder="Nombre del mecánico externo..."
                        className="w-full mt-2 p-2 border rounded"
                      />
                    )}
                  </div>

                  {/* Lista de Mecánicos con Checkboxes */}
                  {!usarMecanicoExterno && (
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-3">
                      {mecanicos.filter(m => m.estado === 'activo').map(m => (
                        <label key={m.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={mecanicosSeleccionados.includes(m.id)}
                            onChange={() => toggleMecanico(m.id)}
                            className="w-4 h-4"
                          />
                          <span className="font-medium">{m.nombre}</span>
                          {m.especialidad && (
                            <span className="text-xs text-gray-500">({m.especialidad})</span>
                          )}
                        </label>
                      ))}
                      {mecanicos.filter(m => m.estado === 'activo').length === 0 && (
                        <p className="text-gray-500 text-sm">No hay mecánicos disponibles</p>
                      )}
                    </div>
                  )}
                  
                  {mecanicosSeleccionados.length > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      {mecanicosSeleccionados.length} mecánico(s) seleccionado(s)
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Descripción *</label>
                <textarea
                  value={nuevoMantenimiento.descripcion}
                  onChange={(e) => setNuevoMantenimiento({...nuevoMantenimiento, descripcion: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows={3}
                  placeholder="Descripción detallada del mantenimiento..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Observaciones</label>
                <textarea
                  value={nuevoMantenimiento.observaciones}
                  onChange={(e) => setNuevoMantenimiento({...nuevoMantenimiento, observaciones: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows={2}
                  placeholder="Observaciones adicionales..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setMostrarModal(false);
                    setBuscadorBus('');
                    setMostrarListaBuses(false);
                    setModoEdicion(false);
                    setMantenimientoSeleccionado(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={modoEdicion ? actualizarMantenimiento : agregarMantenimiento}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={guardando || !nuevoMantenimiento.bus_id || !nuevoMantenimiento.descripcion}
                >
                  {guardando ? 'Guardando...' : (modoEdicion ? 'Actualizar' : 'Crear Mantenimiento')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Detalles */}
      {mostrarDetalles && mantenimientoSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Detalles del Mantenimiento</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">ID</label>
                  <p className="text-lg">{mantenimientoSeleccionado.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Estado</label>
                  <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${getEstadoColor(mantenimientoSeleccionado.estado)}`}>
                    {mantenimientoSeleccionado.estado?.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Tipo</label>
                  <p className="text-lg capitalize">{mantenimientoSeleccionado.tipo}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Bus</label>
                  <p className="text-lg">{getBusNombre(mantenimientoSeleccionado.bus_id)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">📅 Fecha Programada</label>
                  <p className="text-lg">{formatearFecha(mantenimientoSeleccionado.fecha_programada)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">🔧 Fecha de Atención</label>
                  <p className="text-lg">
                    {mantenimientoSeleccionado.fecha_atencion && mantenimientoSeleccionado.fecha_atencion.trim() !== '' ? (
                      <span className="text-green-600">{formatearFecha(mantenimientoSeleccionado.fecha_atencion)}</span>
                    ) : (
                      <span className="text-gray-400 italic">No registrada</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Turno</label>
                  <p className="text-lg capitalize">{mantenimientoSeleccionado.turno}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Categoría</label>
                  <p className="text-lg capitalize">{mantenimientoSeleccionado.categoria}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Mecánico(s)</label>
                <p className="text-lg">{getMecanicosNombres(mantenimientoSeleccionado)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Descripción</label>
                <p className="text-gray-700 whitespace-pre-wrap">{mantenimientoSeleccionado.descripcion}</p>
              </div>

              {mantenimientoSeleccionado.observaciones && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Observaciones</label>
                  <p className="text-gray-700 whitespace-pre-wrap">{mantenimientoSeleccionado.observaciones}</p>
                </div>
              )}

              {mantenimientoSeleccionado.costo && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Costo</label>
                  <p className="text-2xl font-bold text-green-600">S/ {mantenimientoSeleccionado.costo}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                {/* Botones de cambio de estado */}
                <div className="flex gap-2 mr-auto">
                  {mantenimientoSeleccionado.estado !== 'pendiente' && (
                    <button
                      onClick={() => cambiarEstado(mantenimientoSeleccionado.id, 'pendiente')}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                    >
                      <Clock size={14} />
                      Pendiente
                    </button>
                  )}
                  
                  {mantenimientoSeleccionado.estado !== 'en_proceso' && (
                    <button
                      onClick={() => cambiarEstado(mantenimientoSeleccionado.id, 'en_proceso')}
                      className="flex items-center gap-1 px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                    >
                      <Play size={14} />
                      En Proceso
                    </button>
                  )}
                  
                  {mantenimientoSeleccionado.estado !== 'completado' && (
                    <button
                      onClick={() => cambiarEstado(mantenimientoSeleccionado.id, 'completado')}
                      className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                    >
                      <CheckCircle size={14} />
                      Completado
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setMostrarDetalles(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cerrar
                </button>
                <button
                  onClick={() => {
                    setMostrarDetalles(false);
                    editarMantenimiento(mantenimientoSeleccionado);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Editar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Stock */}
      {mostrarStock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Package size={24} />
                Stock de Repuestos
              </h3>
              <button
                onClick={() => setMostrarStock(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* Buscador */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Buscar repuesto..."
                    value={busquedaStock}
                    onChange={(e) => setBusquedaStock(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Lista de stock */}
              {cargandoStock ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : stockItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No hay productos en stock</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stockItems
                        .filter(item => 
                          coincideBusqueda(busquedaStock, item.nombre, item.categoria, item.codigo)
                        )
                        .map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.nombre || 'Sin nombre'}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{item.categoria || 'N/A'}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                (item.stock || 0) <= (item.stock_minimo || 0) 
                                  ? 'bg-red-100 text-red-800' 
                                  : (item.stock || 0) <= (item.stock_minimo || 0) * 2
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {item.stock || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                              S/ {(item.precio || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setMostrarStock(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MantenimientosCompleto;
